import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useFreezableFilter} from 'hooks/useFreezableFilter'
import {useOtaMode} from 'hooks/useOtaMode/useOtaMode'

import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'

import {
  getNeighborhoods,
  getPropertyBrands
} from '../../../modules/sapiSearch/selectors'
import {getIsCheckInAtLeastTwoDaysAway} from '../../../modules/search/selectors'
import {filterMessages} from '../../Filters/filterMessages'
import {useSectionTracker} from '../SectionTracker'
import {AllFiltersCategoryItem} from './CategoryItem'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s500};
  width: 208px;
  padding-left: ${cssTheme.layout.spacing.s700};
  padding-right: ${cssTheme.layout.spacing.s400};
  padding-top: ${cssTheme.layout.spacing.s500};
  padding-bottom: ${cssTheme.layout.spacing.s500};
  overflow-y: auto;
`

interface Props {
  hasRecentlyUsedFilters: boolean
}
export const AllFilterCategoryList: React.FC<Props> = ({
  hasRecentlyUsedFilters
}) => {
  const {activePosition, handleScrollToSection} = useSectionTracker()
  const {formatMessage} = useIntl()
  const {brandName} = useBrand()
  const {
    monetisation: {onlyOnBrandTag}
  } = useBrandCapabilities()
  const {isOTAMode} = useOtaMode()

  const showExclusiveFilter = onlyOnBrandTag.enabled && !isOTAMode
  const exclusiveLabel = onlyOnBrandTag.enabled
    ? formatMessage(onlyOnBrandTag.config.filterSectionLabel, {brandName})
    : ''
  const neighborhoods = useSelector(getNeighborhoods)
  const propertyBrands = useSelector(getPropertyBrands)
  const isCheckInAtLeastTwoDaysAway = useSelector(
    getIsCheckInAtLeastTwoDaysAway
  )

  // Added as a part of c747927f-deal-freeze-srp
  const isFreezableFilterEnabled = useFreezableFilter()

  const menuItemsConfig = [
    ...(hasRecentlyUsedFilters
      ? [
          {
            label: formatMessage(filterMessages.recentlyUsed)
          }
        ]
      : []),
    {
      label: formatMessage(filterMessages.popularTypes)
    },
    {
      label: formatMessage(filterMessages.bookingOptions),
      visible: isCheckInAtLeastTwoDaysAway
    },
    {
      label: formatMessage(filterMessages.freezeDeal),
      visible: isFreezableFilterEnabled
    },
    {
      label: formatMessage(filterMessages.priceFilter)
    },
    {
      label: formatMessage(filterMessages.amenities)
    },
    {
      label: formatMessage(filterMessages.mealPlans)
    },
    {
      label: exclusiveLabel,
      visible: showExclusiveFilter
    },
    {
      label: formatMessage(filterMessages.reviewScore)
    },
    {
      label: formatMessage(filterMessages.hotelStarClass)
    },
    {
      label: formatMessage(filterMessages.propertyType)
    },
    {
      label: formatMessage(filterMessages.neighborhood),
      visible: neighborhoods?.length > 0
    },
    {
      label: formatMessage(filterMessages.propertyBrand),
      visible: propertyBrands?.length > 0
    },
    {
      label: formatMessage(filterMessages.travelerExperience)
    }
  ]

  const menuItems = menuItemsConfig.filter(item => item.visible !== false)

  return (
    <Wrapper>
      {menuItems.map((item, index) => (
        <AllFiltersCategoryItem
          key={item.label}
          active={index === activePosition}
          label={item.label}
          onClick={() => handleScrollToSection(index)}
        />
      ))}
    </Wrapper>
  )
}
